import { getFullName } from "@/components/ComplexFields/PersonInformationComplexField";
import { buildComplexFieldKey, COMPLEX_FIELD_KEYS, FIELD_KEYS } from "@/constants/fieldKeys";
import { KeyPerson } from "@/types/KeyPerson";
import { DigitalBankingRole } from "@/types/enums";
import { AppToasterV2, BaseFieldProps, CoreFieldValue, hashStringSHA256 } from "@ucl/library";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAutoSave } from "./useAutoSave";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";
import KeyPersonApiClient from "@/api/keyPersonApiClient";
import { DebitCardInformation, DigitalBankingInformation } from "@/types";

export const useKeyPerson = (
  financialApplicationId: string,
  keyPersonId: string,
  onApplicableRolesChange?: (value: KeyPerson) => void
) => {
  const keyPersonApiClient = useAuthenticatedApiClient(KeyPersonApiClient);
  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId);
  const [formValue, setFormValueInternal] = useState<KeyPerson | undefined>();
  const [formValueHash, setFormValueHash] = useState<string | undefined>();
  const debitCardInformationRenderKeyRef = useRef<string>("debit-card-information");
  const digitalBankingInformationRenderKeyRef = useRef<string>("digital-banking-information");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isApplicableRolesChange = useRef<boolean>(false);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await saveFormData(formValue);
      }
    },
    debounceDelay: 1000,
  });

  useEffect(() => {
    getKeyPerson();
  }, [financialApplicationId, keyPersonId]);

  const getKeyPerson = async () => {
    setIsLoading(true);
    const keyPerson = await keyPersonApiClient.getKeyPerson(financialApplicationId, keyPersonId);
    debitCardInformationRenderKeyRef.current = keyPerson.debitCardInformation
      ?.debitCardNameDisplayed
      ? "debit-card-true"
      : "debit-card-false";
    digitalBankingInformationRenderKeyRef.current = keyPerson.digitalBankingInformation
      ?.digitalBankingUsername
      ? "digital-banking-true"
      : "digital-banking-false";
    setFormValueInternal(keyPerson);
    const keyPersonHash = await hashStringSHA256(JSON.stringify(keyPerson));
    setFormValueHash(keyPersonHash);
    setIsLoading(false);
    onApplicableRolesChange?.(keyPerson);
  };

  const setFormValue = async (
    value: KeyPerson,
    _newFieldValue: CoreFieldValue,
    fieldProps: BaseFieldProps<CoreFieldValue>
  ) => {
    // Check if the form value has changed
    const newHash = await hashStringSHA256(JSON.stringify(value));
    if (!formValueHash || formValueHash === newHash) {
      return;
    }

    // Set isApplicableRolesChange to true if the applicable roles have changed
    if (
      fieldProps.fieldKey === FIELD_KEYS.KEY_PERSON.IS_BENEFICIAL_OWNER ||
      fieldProps.fieldKey === FIELD_KEYS.KEY_PERSON.IS_AUTHORIZED_SIGNER ||
      fieldProps.fieldKey === FIELD_KEYS.KEY_PERSON.IS_CONTROL_PERSON ||
      fieldProps.fieldKey === FIELD_KEYS.KEY_PERSON.BENEFICIAL_OWNER_PERCENTAGE
    ) {
      isApplicableRolesChange.current = true;
    }

    // Run any UI field changes
    const resolvedValue = resolveFieldForm(value, fieldProps);
    setFormValueInternal({ ...resolvedValue });
    setFormValueHash(newHash);

    // Key Person Applicable Roles Change
    if (isApplicableRolesChange.current) {
      isApplicableRolesChange.current = false;
      onApplicableRolesChange?.(resolvedValue);
    }

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const resolveFieldForm = (
    value: KeyPerson,
    fieldProps: BaseFieldProps<CoreFieldValue>
  ): KeyPerson => {
    const enableClientDefaultHandling = false;

    // If Has Debit Card change to true, set Debit Card Mailing Address to Business Address
    const hasDebitCardInformationKey = buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION,
      COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.HAS_DEBIT_CARD
    );
    if (
      enableClientDefaultHandling &&
      fieldProps.fieldKey === hasDebitCardInformationKey &&
      debitCardInformationRenderKeyRef.current === "debit-card-false" &&
      value.debitCardInformation?.hasDebitCard &&
      value.personInformation
    ) {
      // TODO: Add business information to the form value
      const debitCardNameDisplayed = !value?.debitCardInformation?.debitCardNameDisplayed
        ? getFullName(value.personInformation)
        : "";
      value.debitCardInformation.debitCardNameDisplayed = debitCardNameDisplayed;
      //value.debitCardInformation.debitCardMailingAddress = debitCardMailingAddress;
      debitCardInformationRenderKeyRef.current = "debit-card-true";
    }

    // If Has Digital Banking change to true, populate digital banking fields
    const hasDigitalBankingInformationKey = buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION,
      COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.HAS_DIGITAL_BANKING
    );

    if (
      enableClientDefaultHandling &&
      fieldProps.fieldKey === hasDigitalBankingInformationKey &&
      digitalBankingInformationRenderKeyRef.current === "digital-banking-false" &&
      value.digitalBankingInformation?.hasDigitalBanking &&
      value.personInformation
    ) {
      const digitalBankingUsername = !value?.digitalBankingInformation?.digitalBankingUsername
        ? getFullName(value.personInformation).replace(/\s+/g, "")
        : "";
      const digitalBankingEmailAddress = !value?.digitalBankingInformation
        ?.digitalBankingEmailAddress
        ? value.personInformation?.emailAddress
        : "";
      const digitalBankingPhoneNumber = !value?.digitalBankingInformation?.digitalBankingPhoneNumber
        ? value.personInformation?.phoneNumber
        : "";
      const digitalBankingRole = !value?.digitalBankingInformation?.digitalBankingRole
        ? DigitalBankingRole.User
        : undefined;

      value.digitalBankingInformation.digitalBankingUsername = digitalBankingUsername;
      value.digitalBankingInformation.digitalBankingEmailAddress = digitalBankingEmailAddress;
      value.digitalBankingInformation.digitalBankingPhoneNumber = digitalBankingPhoneNumber;
      value.digitalBankingInformation.digitalBankingRole = digitalBankingRole;
      digitalBankingInformationRenderKeyRef.current = "digital-banking-true";
    }

    return { ...value };
  };

  const saveFormData = useCallback(
    async (data: KeyPerson) => {
      setIsSaving(true);

      try {
        const updated = await keyPersonApiClient.updateKeyPerson(
          financialApplicationId,
          keyPersonId,
          data
        );

        await refreshFinancialApplication();

        // This is a temporary hack to ensure that the form value gets updated with logic from the server, but only for those parts that are server-managed.

        const updates = {
          debitCardInformation: {} as Partial<DebitCardInformation>,
          digitalBankingInformation: {} as Partial<DigitalBankingInformation>,
        };

        let mayHaveServerUpdates = false;

        if (updated.debitCardInformation?.useHomeAddressForDebitCardMailingAddress === true) {
          updates.debitCardInformation.debitCardMailingAddress =
            updated.personInformation?.homeAddress;
          mayHaveServerUpdates = true;
        }

        if (!data.debitCardInformation?.debitCardNameDisplayed) {
          updates.debitCardInformation.debitCardNameDisplayed =
            updated.debitCardInformation?.debitCardNameDisplayed;
          mayHaveServerUpdates = true;
        }

        if (!data.debitCardInformation?.businessNameDisplayedOnCard) {
          updates.debitCardInformation.businessNameDisplayedOnCard =
            updated.debitCardInformation?.businessNameDisplayedOnCard;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingUsername) {
          updates.digitalBankingInformation.digitalBankingUsername =
            updated.digitalBankingInformation?.digitalBankingUsername;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingEmailAddress) {
          updates.digitalBankingInformation.digitalBankingEmailAddress =
            updated.digitalBankingInformation?.digitalBankingEmailAddress;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingPhoneNumber) {
          updates.digitalBankingInformation.digitalBankingPhoneNumber =
            updated.digitalBankingInformation?.digitalBankingPhoneNumber;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingRole) {
          updates.digitalBankingInformation.digitalBankingRole =
            updated.digitalBankingInformation?.digitalBankingRole;
          mayHaveServerUpdates = true;
        }

        if (mayHaveServerUpdates) {
          setFormValueInternal((prev) => ({
            ...prev,
            debitCardInformation: {
              ...prev?.debitCardInformation,
              ...updates.debitCardInformation,
            },
            digitalBankingInformation: {
              ...prev?.digitalBankingInformation,
              ...updates.digitalBankingInformation,
            },
          }));
        }
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Error saving key person data:", error);
        AppToasterV2.showFailedActionToaster("Failed to save key person data.");
      } finally {
        setIsSaving(false);
      }
    },
    [financialApplicationId, keyPersonId]
  );

  return {
    formValue,
    setFormValue,
    saveFormData,
    isSaving,
    isLoading,
    debitCardInformationRenderKey: debitCardInformationRenderKeyRef.current,
    digitalBankingInformationRenderKey: digitalBankingInformationRenderKeyRef.current,
  };
};
